import { Filter } from '@cubejs-client/core';
import { EClientPlatform } from 'components/audience/models/addTriggersModel';
import { format } from 'date-fns';
import { isDefined, isString } from 'lib/typeguards';
import { DateRangePickerValue } from 'ui/MuiDateRangePicker';

export type TimeGranularity = 'month' | 'week' | 'day';

export enum EReviewsMeasureView {
    REVIEWS_METRICS = 'review_metrics',
    ACTION_METRICS = 'action_metrics',
    PROMPT_METRICS = 'prompt_metrics'
}

export enum EReviewsMeasureType {
    COUNT = 'count',
    TOTAL_RATING = 'total_rating',
    AVG = 'avg_rating',
    ONE_STAR_COUNT = 'count_one_star',
    TWO_STAR_COUNT = 'count_two_star',
    THREE_STAR_COUNT = 'count_three_star',
    FOUR_STAR_COUNT = 'count_four_star',
    FIVE_STAR_COUNT = 'count_five_star'
}

export enum EReviewsDimension {
    SCENARIO = 'scenario',
    SOURCE_PLATFORM = 'source_platform'
}

export enum EActionMeasureType {
    ORDER_COUNT = 'count'
}

export enum EPromptMeasureType {
    PROMPT_COUNT = 'prompt_count'
}

export const REVIEWS_DISTRIBUTION_MEASURES = [
    `${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.ONE_STAR_COUNT}`,
    `${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.TWO_STAR_COUNT}`,
    `${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.THREE_STAR_COUNT}`,
    `${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.FOUR_STAR_COUNT}`,
    `${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.FIVE_STAR_COUNT}`
];

export const REVIEWS_DISTRIBUTION_MEASURES_MAP = {
    [`${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.ONE_STAR_COUNT}`]: {
        title: 'One Star',
        shortTitle: '1★',
        key: EReviewsMeasureType.ONE_STAR_COUNT,
        value: 1
    },
    [`${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.TWO_STAR_COUNT}`]: {
        title: 'Two Star',
        shortTitle: '2★',
        key: EReviewsMeasureType.TWO_STAR_COUNT,
        value: 2
    },
    [`${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.THREE_STAR_COUNT}`]: {
        title: 'Three Star',
        shortTitle: '3★',
        key: EReviewsMeasureType.THREE_STAR_COUNT,
        value: 3
    },
    [`${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.FOUR_STAR_COUNT}`]: {
        title: 'Four Star',
        shortTitle: '4★',
        key: EReviewsMeasureType.FOUR_STAR_COUNT,
        value: 4
    },
    [`${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.FIVE_STAR_COUNT}`]: {
        title: 'Five Star',
        shortTitle: '5★',
        key: EReviewsMeasureType.FIVE_STAR_COUNT,
        value: 5
    }
};

export type ReviewsDistributionMeasureKey = keyof typeof REVIEWS_DISTRIBUTION_MEASURES_MAP;

export interface ReviewsMetricsRecord {
    value?: number;
    loading?: boolean;
}

export interface ReviewsMetricsTotal {
    key: EReviewsMeasureType;
    current: ReviewsMetricsRecord;
    retro: ReviewsMetricsRecord;
    difference?: number;
}

/* Common filters for both reviews and prompts */
export function getReviewsAnalyticsFilters(
    view: EReviewsMeasureView,
    selectedLocationIds?: string[],
    platform?: string
): Filter[] {
    const filters: Filter[] = [];
    if (isDefined(selectedLocationIds) && selectedLocationIds.length > 0) {
        filters.push({
            member: `${view}.location_id`,
            operator: 'contains',
            values: selectedLocationIds
        });
    }
    if (isDefined(platform) && isString(platform)) {
        const platforms =
            platform === 'web' ? [EClientPlatform.WEB] : [EClientPlatform.IOS, EClientPlatform.ANDROID];
        filters.push({
            member: `${view}.source_platform`,
            operator: 'contains',
            values: platforms
        });
    }
    return filters;
}

/* Time dimensions for both reviews and prompts */
export function getReviewsAnalyticsTimeDimensions(
    view: EReviewsMeasureView,
    dateRange: DateRangePickerValue,
    timeGranularity?: TimeGranularity
) {
    return [
        {
            dimension: `${view}.created_at`,
            dateRange: [
                format(dateRange.startDate, 'yyyy-MM-dd'),
                format(dateRange.endDate, 'yyyy-MM-dd')
            ] as [string, string],
            granularity: timeGranularity
        }
    ];
}

export function getActionAnalyticsFilters(selectedLocationIds?: string[], platform?: string): Filter[] {
    const filters: Filter[] = [];
    if (isDefined(selectedLocationIds) && selectedLocationIds.length > 0) {
        filters.push({
            member: `${EReviewsMeasureView.ACTION_METRICS}.location_id`,
            operator: 'contains',
            values: selectedLocationIds
        });
    }
    if (isDefined(platform) && isString(platform)) {
        const platforms =
            platform === 'web' ? [EClientPlatform.WEB] : [EClientPlatform.IOS, EClientPlatform.ANDROID];
        filters.push({
            member: `${EReviewsMeasureView.ACTION_METRICS}.client_platform`,
            operator: 'equals',
            values: platforms
        });
    }
    return filters;
}

export function getActionAnalyticsTimeDimensions(
    dateRange: DateRangePickerValue,
    timeGranularity?: TimeGranularity
) {
    return [
        {
            dimension: `${EReviewsMeasureView.ACTION_METRICS}.timestamp`,
            dateRange: [
                format(dateRange.startDate, 'yyyy-MM-dd'),
                format(dateRange.endDate, 'yyyy-MM-dd')
            ] as [string, string],
            granularity: timeGranularity
        }
    ];
}
